import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { LAB_GATE_LABELS, type LabWorklistRow, type LabWorklistState } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  PageHeader,
  Row,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useRef, useState } from 'react';

import { findLabSample, labWorklistQuery, myNotificationsQuery } from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { screen, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { CollectDialog } from '../lab/collect-dialog.tsx';
import { heldBySha, ShaApprovalTag } from '../sha/approval-tag.tsx';
import {
  formatWaiting,
  PRIORITY_LABELS,
  REPORT_STATUS_LABELS,
  WORKLIST_TABS,
} from '../lab/format.ts';
import styles from '../lab/lab.module.css';
import { NotificationDialog } from '../lab/notifications.tsx';
import { formatTimeOnly } from '../patients/format.ts';
import { errorText } from '../stock/common.tsx';

const route = getRouteApi('/_app/lab');

const EMPTY: Record<LabWorklistState, string> = {
  to_collect: 'Nothing to collect. New lab requests appear here once they are placed.',
  in_progress: 'No samples waiting for results.',
  to_validate: 'Nothing waiting for validation.',
  released: 'No reports released today yet.',
};

/**
 * The lab worklist (DESIGN.md §8, plan §8.2): scan or type a sample number at the top; tabs
 * by state; critical results nobody has acknowledged pinned first; a dense table per
 * section, longest wait first. Addresses carry the tab and internal IDs only.
 */
export function LabWorklistPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const { state } = route.useSearch();
  const navigate = useNavigate();
  const tab = state ?? 'to_collect';

  const goTo = (state: LabWorklistState) => () => {
    void navigate({ to: '/lab', search: { state } });
  };
  useShortcut('1', goTo('to_collect'), { description: 'To collect' });
  useShortcut('2', goTo('in_progress'), { description: 'In progress' });
  useShortcut('3', goTo('to_validate'), { description: 'To validate' });
  useShortcut('4', goTo('released'), { description: 'Released today' });

  if (session === null || !can(session, 'lab.read')) {
    return (
      <>
        <PageHeader title="Lab" />
        <Notice tone="info">
          You don't have permission to see the lab worklist. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  return (
    <div className={styles.page}>
      <PageHeader
        title="Lab"
        actions={
          <span className={styles.actions}>
            {can(session, 'lab.qc.read') ? <Link to="/lab/qc">Quality control</Link> : null}
            <Link to="/lab/analyzer-results">Analyzer results</Link>
          </span>
        }
      />
      <ScanField />
      {can(session, 'lab.critical.escalations') ? <EscalatedCriticals /> : null}
      <nav aria-label="Lab worklist" className={styles.tabs}>
        {WORKLIST_TABS.map((t) => (
          <Link
            key={t.state}
            to="/lab"
            search={{ state: t.state }}
            className={styles.tab}
            aria-keyshortcuts={t.key}
            {...(t.state === tab ? { 'aria-current': 'page' as const } : {})}
          >
            {t.label}
          </Link>
        ))}
      </nav>
      <ShortcutHint combo="1" to="4" action="switch between these lists" />
      <Worklist state={tab} canCollect={can(session, 'lab.collect')} />
    </div>
  );
}

function ScanField() {
  const navigate = useNavigate();
  const online = useOnline();
  const [text, setText] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useShortcut('/', () => {
    ref.current?.querySelector('input')?.focus();
  });
  const find = useMutation({
    mutationFn: (sampleNumber: string) => findLabSample(sampleNumber),
    onSuccess: (sample) => {
      setText('');
      void navigate({ to: '/lab/samples/$sampleId', params: { sampleId: sample.id } });
    },
  });
  return (
    <div className={styles.toolbar} ref={ref}>
      <TextField
        label="Scan or type a sample number"
        description="Press / to jump here. A scan opens the sample."
        value={text}
        onChange={(value) => {
          setText(value);
          find.reset();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && text.trim() !== '' && online) find.mutate(text.trim());
        }}
        {...(find.isError
          ? {
              errorMessage: errorText(
                find.error,
                "That sample number couldn't be looked up. Try again.",
              ),
            }
          : {})}
      />
    </div>
  );
}

/** Escalated critical results for the lab to phone through (plan §6.4). */
function EscalatedCriticals() {
  const notifications = useQuery(myNotificationsQuery);
  const [open, setOpen] = useState<string | null>(null);
  const escalated = (notifications.data?.items ?? []).filter(
    (n) => n.asEscalation && n.kind === 'lab_critical_result' && n.status === 'open',
  );
  if (escalated.length === 0) return null;
  const chosen = escalated.find((n) => n.id === open);
  return (
    <section className={styles.section} aria-labelledby="critical-unacknowledged">
      <h2 id="critical-unacknowledged" className={`${styles.sectionHeading} ${styles.critical}`}>
        Critical, not acknowledged
      </h2>
      <ul className={styles.list}>
        {escalated.map((n) => (
          <li key={n.id} className={styles.listRow}>
            <span className={styles.stack}>
              <strong>{n.patient.displayName}</strong>
              <span className="wodi-id">{n.patient.patientNumber}</span>
            </span>
            <span className={styles.critical}>
              {n.testName}: critical, not acknowledged. Phone the clinician.
            </span>
            <Button
              dense
              onPress={() => {
                setOpen(n.id);
              }}
            >
              Record phone call
            </Button>
          </li>
        ))}
      </ul>
      {chosen === undefined ? null : (
        <NotificationDialog
          notification={chosen}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </section>
  );
}

function bySection(rows: readonly LabWorklistRow[]) {
  const groups = new Map<string, { name: string; rows: LabWorklistRow[] }>();
  for (const row of rows) {
    const group = groups.get(row.sectionId) ?? { name: row.sectionName, rows: [] };
    group.rows.push(row);
    groups.set(row.sectionId, group);
  }
  return [...groups.entries()];
}

/**
 * The collect dialog lives outside the list: collecting the last request empties the tab,
 * and the labels must stay on screen to be printed and reprinted.
 */
function Worklist({ state, canCollect }: { state: LabWorklistState; canCollect: boolean }) {
  const [collecting, setCollecting] = useState<LabWorklistRow | null>(null);
  return (
    <>
      <WorklistTables state={state} canCollect={canCollect} onCollect={setCollecting} />
      {collecting === null ? null : (
        <CollectDialog
          visitId={collecting.visitId}
          patientName={collecting.patient.displayName}
          onClose={() => {
            setCollecting(null);
          }}
        />
      )}
    </>
  );
}

function WorklistTables({
  state,
  canCollect,
  onCollect,
}: {
  state: LabWorklistState;
  canCollect: boolean;
  onCollect: (row: LabWorklistRow) => void;
}) {
  const list = useQuery(labWorklistQuery(state));
  if (list.isPending) return <LoadingBlock label="Loading the worklist" rows={5} />;
  if (list.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void list.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(list.error, "The worklist couldn't be loaded. Try again.")}
      </Notice>
    );
  if (list.data.items.length === 0) return <EmptyState>{EMPTY[state]}</EmptyState>;
  return (
    <>
      {bySection(list.data.items).map(([sectionId, group]) => (
        <section
          key={sectionId}
          className={`${styles.section} ${screen.panel}`}
          aria-labelledby={`s-${sectionId}`}
        >
          <h2 id={`s-${sectionId}`} className={styles.sectionHeading}>
            {group.name}
          </h2>
          <div className={styles.worklistTable}>
            <TableScroll>
              <Table flat aria-labelledby={`s-${sectionId}`} dense>
                <TableHeader>
                  <Column isRowHeader>Sample no.</Column>
                  <Column>Patient</Column>
                  <Column>Test</Column>
                  <Column>Priority</Column>
                  <Column>{state === 'to_collect' ? 'Requested' : 'Collected'}</Column>
                  <Column numeric>Waiting</Column>
                  <Column>Status</Column>
                </TableHeader>
                <TableBody>
                  {group.rows.map((row) => (
                    <Row key={row.orderId} id={row.orderId}>
                      <Cell>
                        {row.sample === null ? (
                          <span className={styles.meta}>Not collected</span>
                        ) : (
                          <Link
                            to="/lab/samples/$sampleId"
                            params={{ sampleId: row.sample.id }}
                            className="wodi-id"
                          >
                            {row.sample.sampleNumber}
                          </Link>
                        )}
                      </Cell>
                      <Cell>
                        <span className={styles.stack}>
                          <span>{row.patient.displayName}</span>
                          <span className={`wodi-id ${styles.meta}`}>
                            {row.patient.patientNumber}
                          </span>
                        </span>
                      </Cell>
                      <Cell>{row.testName}</Cell>
                      <Cell>
                        {row.priority === 'urgent' ? (
                          <Tag state="active">{PRIORITY_LABELS.urgent}</Tag>
                        ) : (
                          PRIORITY_LABELS.routine
                        )}
                      </Cell>
                      <Cell>
                        {formatTimeOnly(
                          row.sample === null ? row.orderedAt : row.sample.collectedAt,
                        )}
                      </Cell>
                      <Cell numeric>
                        {formatWaiting(row.minutesWaiting)}
                        {row.overTarget ? (
                          <span className={`${styles.meta} ${styles.overTarget}`}>
                            {' '}
                            Over target
                          </span>
                        ) : null}
                      </Cell>
                      <Cell>
                        <RowStatus
                          row={row}
                          state={state}
                          canCollect={canCollect}
                          onCollect={() => {
                            onCollect(row);
                          }}
                        />
                      </Cell>
                    </Row>
                  ))}
                </TableBody>
              </Table>
            </TableScroll>
          </div>
        </section>
      ))}
    </>
  );
}

function RowStatus({
  row,
  state,
  canCollect,
  onCollect,
}: {
  row: LabWorklistRow;
  state: LabWorklistState;
  canCollect: boolean;
  onCollect: () => void;
}) {
  if (row.criticalUnacknowledged) {
    return <span className={styles.critical}>Critical, not acknowledged</span>;
  }
  if (state === 'to_collect') {
    return (
      <span className={styles.actions}>
        {heldBySha(row.shaApproval) ? null : <span>{LAB_GATE_LABELS[row.gate]}</span>}
        <ShaApprovalTag approval={row.shaApproval} />
        {canCollect && row.gate === 'ready' ? (
          <Button dense onPress={onCollect}>
            Collect
          </Button>
        ) : null}
      </span>
    );
  }
  return <span>{row.reportStatus === null ? '' : REPORT_STATUS_LABELS[row.reportStatus]}</span>;
}
